import {
    type HTTPHeaders,
    type HTTPLinkOptions,
    httpBatchStreamLink,
    httpLink,
    type Operation,
    splitLink,
    type TRPCLink,
} from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { SuperJSON } from 'superjson';

type RequestOperations = readonly [Operation, ...Operation[]];

type CreateHttpTransportLinkOptions = {
    url: string;
    headers: (opList: RequestOperations) => HTTPHeaders | Promise<HTTPHeaders>;
    fetch?: HTTPLinkOptions<AnyRouter['_def']['_config']['$types']>['fetch'];
};

/**
 * Keep ordinary traffic on the shared streaming batch while isolating four kinds of
 * operation that must own their HTTP request:
 *
 * - Organisation overrides: the server creates one organisation context per HTTP
 *   request, so an operation captured before an organisation switch must never share
 *   a request with operations using the live organisation.
 * - Cancellable streams (`context.cancellableStream`): tRPC only aborts a shared
 *   batch fetch after every operation in it aborts, so these use a dedicated
 *   single-item batch-stream request that their AbortSignal owns.
 * - Single-use CAPTCHA operations (`context.captchaSingleUse`): the server validates
 *   the CAPTCHA header once per HTTP request and spends the verdict on a single
 *   operation (`publicProcedureWithCaptcha`), so a batched sibling would be rejected
 *   as reusing the credential. Each isolated request mints and carries its own token
 *   via `getPublicFormCaptchaTokensForOperation` during header construction.
 * - E2E-selected procedures (`window.__E2E_ISOLATED_TRPC_PROCEDURES__`): the test
 *   runner marks only these requests with its denial marker, leaving unrelated
 *   prefetches on the shared batch. No credential is supplied by this browser knob.
 *
 * `context.captcha` alone deliberately does NOT isolate: it only mints the CAPTCHA
 * header. Routes like `file.getUploadPresignedUrl` check the request-level verdict
 * without spending it, so N per-file upload operations can keep batching into one
 * HTTP request under a single credential instead of serialising N widget mints.
 */
export function createHttpTransportLink<TRouter extends AnyRouter>({
    url,
    headers,
    fetch,
}: CreateHttpTransportLinkOptions): TRPCLink<TRouter> {
    const sharedOptions = {
        url,
        methodOverride: 'POST' as const,
        transformer: SuperJSON,
        ...(fetch ? { fetch } : {}),
    };

    return splitLink({
        condition: (op) => op.context.orgIdOverride !== undefined || op.context.captchaSingleUse === true,
        true: httpLink<AnyRouter>({
            ...sharedOptions,
            headers: ({ op }) => headers([op]),
        }),
        false: splitLink({
            // E2E denial assertions must not share a request-level marker with unrelated
            // prefetches. Keep streaming semantics, but give each selected operation its
            // own request. This browser knob supplies no secret or server exemption.
            condition: (op) =>
                op.context.cancellableStream === true ||
                (typeof window !== 'undefined' && window.__E2E_ISOLATED_TRPC_PROCEDURES__?.includes(op.path) === true),
            true: httpBatchStreamLink<AnyRouter>({
                ...sharedOptions,
                maxItems: 1,
                maxURLLength: 10000,
                headers: ({ opList }) => headers(opList),
            }),
            false: httpBatchStreamLink<AnyRouter>({
                ...sharedOptions,
                maxItems: 50,
                maxURLLength: 10000,
                headers: ({ opList }) => headers(opList),
            }),
        }),
    });
}
